import {memo, useContext, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {atomButtonDesigns, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import {useRouter} from '@s-ui/react-router'
import ActionsAdDiscard, {actionsAdDiscardTypes} from '@adv-mt/mt-actions-ad-discard'
import ActionAdFavorite, {
  actionAdFavoriteButtonSizes,
  actionAdFavoriteTrackingOrigins,
  actionAdFavoriteTypes
} from '@adv-mt/mt-actions-ad-favorite'
import ActionsAdShare, {
  actionAdShareButtonSizes,
  actionAdShareTrackingOrigins,
  actionAdShareTypes
} from '@adv-mt/mt-actions-ad-share'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import NavigationActions, {navigationActionsTrackingData, navigationClickActions} from '@adv-mt/mt-navigation-actions'
import IconArrowLeft from '@adv-mt/mt-svg-icons/lib/ArrowLeft'
import TitleBasic, {titleBasicColors, titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import FavoriteLeadPopover from './favorite-lead-popover.js'

const {FLAT} = atomButtonDesigns

const LinkFactoryDefaultProp = ({children, ...rest} = {}) => <a {...rest}>{children}</a>

const BASE_CLASS = 'mt-NavigationAdToolbar'
const classNameActions = `${BASE_CLASS}-actions`
const classNameInfo = `${BASE_CLASS}-info`
const classNameInfoPrice = `${BASE_CLASS}-infoPrice`
const classNameInfoTitle = `${BASE_CLASS}-infoTitle`
const firstSegmentClassName = `${BASE_CLASS}-firstSegment`
const firstSegmentGroupClassName = `${firstSegmentClassName}Group`

const NavigationAdToolbar = ({
  ad,
  adMonthlyFee,
  contractId,
  defaultLinks = [],
  extendedTrackingData = {},
  hasDiscardButton,
  isFavorite = false,
  isFavoriteLeadEnabled = false,
  linkFactory: Link = LinkFactoryDefaultProp,
  onClickBack = () => {},
  onClickNext = () => {},
  onClickPrev = () => {},
  onFavoriteLeadConfirm = () => {}
}) => {
  const {domain, i18n} = useContext(Context)

  const [currentPosition, setCurrentPosition] = useState(0)
  const [hrefs, setHrefs] = useState({})
  const [isFavoriteLeadPopoverOpen, setIsFavoriteLeadPopoverOpen] = useState(false)
  const {track} = useAnalyticsTrack()

  const router = useRouter()
  const shouldGoBack = router?.location?.state?.shouldGoBack

  const config = domain.get('config')
  const isMobile = config.get('isMobile')

  const companyUrl = config.get('COMPANY_URL')
  const adsListPathPattern = config.get('page').SECOND_HAND_PATH_PATTERN
  const {HIT_ORIGINS} = config.get('tracking')

  const initialMediaQueryMatch = {
    defaultMatches: !isMobile
  }
  const isTabletOrHigher = useMediaQuery(useMediaQueryBreakpoints.sm, initialMediaQueryMatch)

  const referrer = `${companyUrl}${ad.url}`

  const ACTION_HANDLERS = {
    [navigationClickActions.BACK_ACTION]: onClickBack
  }

  const getDefaultBackUrl = () => {
    const [{url: baseUrl} = {}, {url: makeUrl} = {}, {url: makeModelUrl} = {}, {url: makeModelProvinceUrl} = {}] =
      defaultLinks

    return makeModelProvinceUrl || makeModelUrl || makeUrl || baseUrl || adsListPathPattern
  }

  useMount(() => {
    domain
      .get('get_navigation_links_ads_use_case')
      .execute({currentPosition, id: ad.id})
      .then(([navigationLinksError, navigationLinksResponse]) => {
        if (navigationLinksError) return

        const {
          currentPosition: newCurrentPosition,
          backUrl: hrefBack,
          nextUrl: hrefNext,
          prevUrl: hrefPrev
        } = navigationLinksResponse

        setHrefs({hrefBack, hrefNext, hrefPrev})
        setCurrentPosition(newCurrentPosition)
      })
  })

  const handleShareAction = ({url}) => {
    window.open(url, '_blank')
  }

  const handleFavoriteAction = isAddingFavorite => {
    setIsFavoriteLeadPopoverOpen(isAddingFavorite)
  }

  const handleFavoriteLeadPopoverClose = () => setIsFavoriteLeadPopoverOpen(false)

  const onClickAction = ({action}) => {
    const {event} = navigationActionsTrackingData[action]
    track({event})
    const clickAction = ACTION_HANDLERS[action]
    clickAction()
  }

  const backButtonHref = hrefs.hrefBack || getDefaultBackUrl()

  const trackingData = Link()?.props?.state

  const handleBackClick = () => {
    onClickAction({
      action: navigationClickActions.BACK_ACTION
    })

    // When "save_navigation_data_ads_use_case" is used in the origin page before navigating to detail,
    // using the href creates a new history entry instead of going back. This causes the browser back
    // button to return to the detail page again (navigation loop). Use router.goBack() to avoid this.
    if (shouldGoBack) {
      router.goBack()
    }
  }

  const handleDiscardAd = () => {
    router.push({
      pathname: backButtonHref,
      state: {
        discardAd: ad.id
      }
    })
  }

  return (
    <div className={`${BASE_CLASS} ${BASE_CLASS}--topbar`}>
      <div className={firstSegmentClassName}>
        <div className={`${firstSegmentGroupClassName} ${firstSegmentGroupClassName}--topbar`}>
          <AtomButton
            color="primary"
            design={FLAT}
            href={shouldGoBack ? undefined : backButtonHref}
            leftIcon={<IconArrowLeft />}
            link={!shouldGoBack}
            linkFactory={Link}
            onClick={handleBackClick}
            size={atomButtonSizes.LARGE}
            shape={atomButtonShapes.CIRCULAR}
            aria-label={i18n.t('COMMON.GO_BACK')}
          />
        </div>

        {isTabletOrHigher && (
          <div className={classNameInfo}>
            {ad.price ? (
              <div className={classNameInfoPrice}>
                <TitleBasic
                  as={titleBasicTags.h5}
                  color={titleBasicColors.currentColor}
                  size={titleBasicSizes.xs}
                  title={i18n.c(ad.price)}
                />
              </div>
            ) : (
              <>
                {adMonthlyFee ? (
                  <div className={classNameInfoPrice}>
                    <TitleBasic
                      as={titleBasicTags.h5}
                      color={titleBasicColors.currentColor}
                      size={titleBasicSizes.xs}
                      title={i18n.t('COMMON.MONTHLY_FEE', {
                        amount: i18n.c(adMonthlyFee)
                      })}
                    />
                  </div>
                ) : null}
              </>
            )}
            <div className={classNameInfoTitle}>
              <TitleBasic as={titleBasicTags.h5} size={titleBasicSizes.xs} title={ad.title} maxTitleLines={1} />
            </div>
          </div>
        )}
      </div>
      <div className={classNameActions}>
        <>
          {hasDiscardButton && (
            <ActionsAdDiscard
              adId={ad.id}
              isFavorite={isFavorite}
              onAfterDiscard={handleDiscardAd}
              origin={HIT_ORIGINS.AD_DETAIL_AD_DISCARDED}
              size="large"
              type={isTabletOrHigher ? actionsAdDiscardTypes.INLINE_BUTTON : actionsAdDiscardTypes.ICON_BUTTON}
            />
          )}
          <FavoriteLeadPopover
            isOpen={isFavoriteLeadEnabled && isFavoriteLeadPopoverOpen}
            onClose={handleFavoriteLeadPopoverClose}
            onConfirm={onFavoriteLeadConfirm}
          >
            <ActionAdFavorite
              ad={ad}
              extendedTrackingData={extendedTrackingData}
              isFavorite={isFavorite}
              isOnClickCallbackReady={isFavoriteLeadEnabled}
              onClick={handleFavoriteAction}
              size={actionAdFavoriteButtonSizes.LARGE}
              trackingOrigin={actionAdFavoriteTrackingOrigins.DETAIL_TOPBAR_AD_FAVORITED}
              type={isTabletOrHigher ? actionAdFavoriteTypes.INLINE_BUTTON : actionAdFavoriteTypes.ICON_BUTTON}
            />
          </FavoriteLeadPopover>
          <ActionsAdShare
            ad={ad}
            contractId={contractId}
            extendedTrackingData={extendedTrackingData}
            onAction={handleShareAction}
            referer={referrer}
            size={actionAdShareButtonSizes.LARGE}
            trackingData={trackingData}
            trackingOrigin={actionAdShareTrackingOrigins.DETAIL_TOPBAR_AD_SHARED}
            type={isTabletOrHigher ? actionAdShareTypes?.INLINE_BUTTON : actionAdShareTypes?.ICON_BUTTON}
          />
        </>
        <NavigationActions {...hrefs} linkFactory={Link} onClickNext={onClickNext} onClickPrev={onClickPrev} />
      </div>
    </div>
  )
}

NavigationAdToolbar.displayName = 'NavigationAdToolbar'
NavigationAdToolbar.propTypes = {
  /**
   * Ad object containing the ad's details
   */
  ad: PropTypes.object.isRequired,
  /**
   * the ad's monthly fee
   */
  adMonthlyFee: PropTypes.number,
  /**
   * the seller contract id
   */
  contractId: PropTypes.string,
  /**
   * URL to be added on the back link
   */
  defaultLinks: PropTypes.array,
  /**
   * Extended tracking data to be sent with the ad
   */
  extendedTrackingData: PropTypes.object,
  /**
   * boolean prop to define whether to show the discard button
   */
  hasDiscardButton: PropTypes.bool,
  /**
   * boolean prop to define whether it's already a favorite ad for the user
   */
  isFavorite: PropTypes.bool,
  /**
   * boolean prop to enable the post-favorite lead popover (MTR-117126)
   */
  isFavoriteLeadEnabled: PropTypes.bool,
  /**
   * boolean prop to control stickyness
   */
  isSticky: PropTypes.bool,
  /**
   * Factory used to create navigation links
   */
  linkFactory: PropTypes.func,
  /**
   * Callback on click Back button
   */
  onClickBack: PropTypes.func,
  /**
   * Callback on click Next button
   */
  onClickNext: PropTypes.func,
  /**
   * Callback on click Previous button
   */
  onClickPrev: PropTypes.func,
  /**
   * Callback fired when the post-favorite lead popover CTA is confirmed (MTR-117126)
   */
  onFavoriteLeadConfirm: PropTypes.func
}

LinkFactoryDefaultProp.propTypes = {
  children: PropTypes.object
}

const MemoNavigationAdToolbar = memo(NavigationAdToolbar)
MemoNavigationAdToolbar.displayName = 'NavigationAdToolbar'

export default MemoNavigationAdToolbar
// Exposed so the new ad detail's own toolbar (app/src/pages/Detail/variants/new) can wrap its
// favorite button with the exact same post-favorite lead popover legacy uses, instead of forking it.
export {default as FavoriteLeadPopover} from './favorite-lead-popover.js'
